import * as React from 'react';
import Dexie from 'dexie';
import { Provider } from 'react-redux';
import store from '../../redux/store';
import Navigation from './Navigation';
import { useContext, useState } from 'react';

import DataProvider from './DataProvider';
import Loader from './Loader';
import styled from 'styled-components';
import FlexContainer from './FlexContainer';
import { COLOR_CARD_STATUS, COLOR_CONFIG_DEFAULT } from '../../constant';

export const LIMIT_STREAK_UP = 5;
export const LIMIT_STREAK_DOWN = -5;

export interface WordDB {
  id: string;
  isBookmarked: number;
  isStudied: number;
  isExam: number;
  failed: number;
  passed: number;
  streak: number;
  streakResults: number[];
  streakDates: number[];
  isWeak: number;
  isTerrible: number;
  isStrong: number;
  isExcellent: number;
  lessonId: number;
  name: string;
  lastSeen: string;
  createdOn: string;
}

export interface SentenceDB {
  id: string;
  isStudied: number;
  name: string;
}

export interface ConfigDB {
  bookmarkMode: BookmarkModes;
  flashCardsHidePinyin: HideOption;
  flashCardsHideType: HideOption;
  flashCardsHideAudio: HideOption;
  flashCardsHideEasy: HideOption;
  flashCardsHideToneColor: HideOption;
  flashCardsMode: FlashcardModes;
  phrasebookMode: PhrasebookModes;
  collectionsHide: number;
  studyCollection: number;
  colorTones: string[];
  colorCards: string[];
  grammarHideKnown: HideOption;
}

export interface GrammarPointDB {
  id: string;
  isKnown: number;
  title: string;
  lastSeen: string;
  createdOn: string;
}

export enum FlashcardModes {
  Study = 'study',
  Exam = 'exam',
  Review = 'review',
}

export enum PhrasebookModes {
  Pinyin = 'pinyin',
  Hanzi = 'hanzi',
}

export enum BookmarkModes {
  Pinyin = 'pinyin',
  Hanzi = 'hanzi',
}

export enum HideOption {
  On = 1,
  Off = 0,
}

const db = new Dexie('chineseDB');
let isConfigLoaded: boolean = false;

db.version(2.9).stores({
  word: '++id, isBookmarked, isStudied, isExam, failed, passed, streak, lessonId, isWeak, isStrong, isTerrible, isExcellent, name, streakResults, streakDates, lastSeen, createdOn',
  sentences: '++id, isStudied, name',
  grammar_point: '++id, isKnown, title, lastSeen, createdOn',
  config:
    '++id, bookmarkMode, flashCardsHidePinyin, flashCardsHideType, flashCardsHideAudio, flashCardsHideEasy, flashCardsHideToneColor, flashCardsMode, phrasebookMode, collectionsHide, studyCollection, colorTones, colorCards, grammarHideKnown',
});

db.on('ready', async () => {
  const configCount = await db.table('config').count();
  if (configCount === 0) {
    initConfig().then(() => {
      isConfigLoaded = false;
    });
  } else {
    await checkForUpdates();
    isConfigLoaded = false;
  }
});

async function checkForUpdates() {
  const config = await db.table('config').where('id').equals(USER).first();

  if (!config?.colorTones) {
    db.table('config').update(USER, {
      colorTones: COLOR_CONFIG_DEFAULT,
    });
  }

  if (!config?.colorCards) {
    db.table('config').update(USER, {
      colorCards: COLOR_CARD_STATUS,
    });
  }
}

async function initConfig() {
  try {
    await db.table('config').add({
      id: 'xavi',
      bookmarkMode: BookmarkModes.Pinyin,
      flashCardsHidePinyin: HideOption.Off,
      flashCardsHideAudio: HideOption.Off,
      flashCardsHideType: HideOption.Off,
      flashCardsHideEasy: HideOption.Off,
      flashCardsHideToneColor: HideOption.Off,
      flashCardsMode: FlashcardModes.Study,
      phrasebookMode: PhrasebookModes.Hanzi,
      collectionsHide: HideOption.Off,
      studyCollection: 1,
      colorTones: COLOR_CONFIG_DEFAULT,
      colorCards: COLOR_CARD_STATUS,
    });
  } catch (error) {
    throw Error(`Failed to init config: ${error}`);
  }
}

export const DexieContext = React.createContext(db);

export const USER = 'xavi';

const Background = styled.div`
  display: flex;
  background-image: linear-gradient(to right, #0f0c29, #302b63, #24243e);
  min-width: 100%;
  min-height: 100%;
  position: absolute;
  top: 0;
  right: 0;
`;

const PageWrapper = ({ element }: { element: any }) => {
  const db = useContext(DexieContext);
  const [isLoadingData, setLoadingData] = useState<boolean>(true);

  const isReady = () => !isConfigLoaded && !isLoadingData;

  return (
    <DexieContext.Provider value={db}>
      <Provider store={store}>
        <>
          <Navigation />
          <DataProvider
            onReady={() => {
              setLoadingData(false);
            }}
          />
          <Background>
            {isReady() ? (
              element
            ) : (
              <FlexContainer
                style={{ position: 'absolute', width: '100%', height: '100%' }}
                align="center"
                justify="center"
              >
                <Loader />
              </FlexContainer>
            )}
          </Background>
        </>
      </Provider>
    </DexieContext.Provider>
  );
};

export default PageWrapper;
